import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { Paginator } from '@/components/Paginator/Paginator';
import { Button } from '@/components/Button/Button';
import { Box } from '@/components/Box/Box';
import { DatePicker } from '@/components/form/fields/DatePicker/DatePicker';
import { Typography } from '@/components/Typography/Typography';
import { useReplaceSearchParams } from '@/hooks/useReplaceSearchParams';
import { getListData, showSnackbarResponseError } from '@/helpers/helpers';
import { getFileNameFromHeader } from '@/helpers/fileHelpers';
import { AUDIT_CATEGORY, AuditEventType } from '@/types/auditLogTypes';
import {
  AUDIT_LOG_QUERY_KEYS,
  AuditFiltersType,
  exportAuditEvents,
  loadAuditCatalog,
  loadAuditEvents,
} from './api/api';
import { ALL_CATEGORIES, AUDIT_PILLS, EXPORT_FILE_NAME } from './const/const';
import { AuditEventsList } from './ui/AuditEventsList';
import { AuditEventDetailsModal } from './ui/AuditEventDetailsModal';
import { SearchWrapper, Toolbar } from './styles';
import { saveAs } from 'file-saver';
import { Notification } from '@/components/Notification/Notification';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Pills } from '@/components/Pills/Pills';
import { Pill, PillCount } from '@/components/Pills/styles';
import { SearchInput } from '@/bundle/shared/components/SearchInput/SearchInput';
import { useSearch } from '@/bundle/shared/hooks/useSearch';

type ParamsType = AuditFiltersType & { tab?: string };

const toIso = (date: Date | null, endOfDay = false) => {
  if (!date) return null;

  const copy = new Date(date);

  if (endOfDay) copy.setHours(23, 59, 59, 999);
  else copy.setHours(0, 0, 0, 0);

  return copy.toISOString();
};

export const AuditLogPage = () => {
  const { replaceSearchParams, resetSearchParams } = useReplaceSearchParams<ParamsType>();
  const [selected, setSelected] = useState<AuditEventType | null>(null);
  const { search, searchParams, submitSearch, clearSearch, setSearch } = useSearch<ParamsType>('q');

  const category = (searchParams.category as string) || ALL_CATEGORIES;
  const q = (searchParams.q as string) || '';
  const isInsuredTab = category === AUDIT_CATEGORY.INSURED;

  const listParams: AuditFiltersType = {
    ...searchParams,
    category: category === ALL_CATEGORIES ? undefined : category,
    q: q || undefined,
  };

  const { data: catalogResponse } = useQuery({ queryKey: AUDIT_LOG_QUERY_KEYS.catalog, queryFn: loadAuditCatalog });
  const counts = Object.fromEntries((catalogResponse?.body?.categories ?? []).map((row) => [row.key, row.count]));

  const { data: eventsResponse, isPending } = useQuery({
    queryKey: [AUDIT_LOG_QUERY_KEYS.events, listParams],
    queryFn: () => loadAuditEvents(listParams),
    placeholderData: keepPreviousData,
  });

  const { mutate: exportCsv, isPending: isExporting } = useMutation({
    mutationKey: ['audit_log_export'],
    mutationFn: () => exportAuditEvents(listParams),
    onSuccess(response) {
      if (response?.error) return showSnackbarResponseError(response.error);

      const header = response.headers.contentDisposition;
      const fileName = getFileNameFromHeader(header, EXPORT_FILE_NAME);

      saveAs(response?.body, fileName);
    },
  });

  const { list, totalCount, hasData, hasPagination } = getListData(eventsResponse?.body, searchParams);

  const pickCategory = (key: string) =>
    replaceSearchParams({ category: key === ALL_CATEGORIES ? null : key, page: null });

  const hasRequestParam = !!searchParams.request_id;

  return (
    <ContentLayout
      title='Audit log'
      breadcrumbs='Audit Log'
      subtitle='Append-only record of every share, view, verification, consent, vault change and user action.'
      headerAction={
        <Button width={150} mobileStretch disabled={isExporting || !hasData} onClick={exportCsv}>
          Export CSV
        </Button>
      }
    >
      <PageBody>
        <Toolbar>
          <Pills role='tablist' aria-label='Audit log categories'>
            {AUDIT_PILLS.map((pill) => (
              <Pill
                key={pill.key}
                role='tab'
                aria-selected={category === pill.key}
                $active={category === pill.key}
                onClick={() => pickCategory(pill.key)}
              >
                {pill.label}
                {pill.key !== ALL_CATEGORIES && counts[pill.key] !== undefined && (
                  <PillCount>{counts[pill.key]}</PillCount>
                )}
              </Pill>
            ))}
          </Pills>
        </Toolbar>

        <Box display='flex' gap='20px' marginBottom='32px'>
          <SearchWrapper>
            <SearchInput
              variant='small'
              name='audit-search'
              hasErrorComponent={false}
              value={search}
              placeholder='Search by query'
              aria-label='Search by query'
              onClear={clearSearch}
              onChange={(value) => setSearch(value)}
              onSubmit={submitSearch}
            />
          </SearchWrapper>
          <DatePicker
            value={searchParams.date_from ? new Date(searchParams.date_from as string) : null}
            placeholder='From'
            variant='small'
            hasErrorComponent={false}
            onChange={(date) => replaceSearchParams({ date_from: toIso(date), page: null })}
          />
          <DatePicker
            value={searchParams.date_to ? new Date(searchParams.date_to as string) : null}
            placeholder='To'
            variant='small'
            hasErrorComponent={false}
            onChange={(date) => replaceSearchParams({ date_to: toIso(date, true), page: null })}
          />
        </Box>

        {hasRequestParam && (
          <Notification variant='info' verticalAlign='center' inline marginBottom='20px'>
            <Box display='flex' alignItems='center' gap={'16px'}>
              <Typography variant='bodySmall' fontWeight='medium'>
                Only audit logs for action ID <b> {searchParams.request_id} </b> are shown.
              </Typography>
              <ButtonNew variant='secondary' onClick={() => resetSearchParams()}>
                Reset
              </ButtonNew>
            </Box>
          </Notification>
        )}

        {isInsuredTab && (
          <Box marginBottom='16px'>
            <Typography variant='bodySmall' marginBottom='12px'>
              One folder per insured transaction. Each holds the evidence package that proves the transaction was
              secured, including the blockchain anchors.
            </Typography>
          </Box>
        )}

        <AuditEventsList list={list} isLoading={isPending} onOpen={setSelected} />
      </PageBody>

      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
        </PageFooter>
      )}

      {selected && <AuditEventDetailsModal isOpen event={selected} onClose={() => setSelected(null)} />}
    </ContentLayout>
  );
};
